

/**
 * StreakCard
 *
 * Live daily-login streak widget. Reads from GET /api/rewards/streak and
 * claims via POST /api/rewards/daily-login.
 *
 * Shows 7-day marker grid, current/longest streak, and a claim button when
 * today's reward is still available.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { authFetch } from '@/lib/auth-client';
import { cn } from '@/lib/utils';

interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  totalClaims: number;
  lastClaimDate: string | null;
  todayClaimed: boolean;
  nextClaimAvailableAt: string | null;
}

interface ClaimResult {
  awarded: boolean;
  reason?: string;
  newStreak?: number;
  baseCoins?: number;
  bonusCoins?: number;
  bonusType?: string;
  totalCoins?: number;
}

// Day → marker label / bonus indicator. Matches the seeded reward catalog.
const DAY_MARKERS = [
  { day: 1, baseCoins: 15 },
  { day: 2, baseCoins: 15 },
  { day: 3, baseCoins: 15, bonus: 25 },
  { day: 4, baseCoins: 15 },
  { day: 5, baseCoins: 15 },
  { day: 6, baseCoins: 15 },
  { day: 7, baseCoins: 15, bonus: 75 },
];

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function CoinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12M9 9.5c0-.83 1.34-1.5 3-1.5s3 .67 3 1.5S14.66 11 12 11 9 11.67 9 12.5s1.34 1.5 3 1.5 3-.67 3-1.5" />
    </svg>
  );
}

export function StreakCard({ className }: { className?: string }) {
  const updateBalance = useAuthStore((s) => s.updateBalance);
  const userCoins = useAuthStore((s) => s.user?.coins ?? 0);
  const [info, setInfo] = useState<StreakInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/api/rewards/streak');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setInfo((data.data ?? data) as StreakInfo);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load streak');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleClaim = useCallback(async () => {
    if (claiming || !info || info.todayClaimed) return;
    setClaiming(true);
    setError(null);
    try {
      const res = await authFetch('/api/rewards/daily-login', { method: 'POST' });
      const body = await res.json();
      const result = (body.data ?? body) as ClaimResult;

      if (!res.ok || !result.awarded) {
        setError(result.reason ?? body.error ?? 'Failed to claim');
        return;
      }

      // Optimistically update balance + show toast
      updateBalance({ coins: userCoins + (result.totalCoins ?? 0) });
      setToast(
        result.bonusCoins && result.bonusCoins > 0
          ? `+${result.totalCoins}c (${result.baseCoins} daily + ${result.bonusCoins} streak bonus)`
          : `+${result.totalCoins}c daily reward`,
      );
      setTimeout(() => setToast(null), 4000);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setClaiming(false);
    }
  }, [claiming, info, load, updateBalance, userCoins]);

  if (loading) {
    return (
      <div className={cn('rounded-lg border border-surface-3 bg-surface-2 p-3', className)}>
        <div className="h-24 bg-surface-3/30 rounded animate-pulse" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className={cn('rounded-lg border border-surface-3 bg-surface-2 p-3', className)}>
        <div className="flex items-center gap-1.5 mb-1">
          <FlameIcon className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-xs font-bold text-cream">Daily Streak</span>
        </div>
        <p className="text-[11px] text-warm-gray/50">Unable to load streak ({error})</p>
      </div>
    );
  }

  if (!info) return null;

  // Highlight which day in the 7-marker visualization the user is "on"
  // If they haven't claimed today yet, the next-up day is current+1
  const displayedDay = info.todayClaimed ? info.currentStreak : info.currentStreak + 1;
  const nextDayCoins = DAY_MARKERS[(displayedDay - 1) % 7];

  return (
    <div className={cn('rounded-lg border border-surface-3 bg-surface-2 p-3', className)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <FlameIcon className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-xs font-bold text-cream">Daily Streak</span>
          <span className="text-[10px] text-warm-gray/50">{info.currentStreak}d</span>
        </div>
        <span className="text-[9px] text-warm-gray/40">Best: {info.longestStreak}d &middot; {info.totalClaims} total</span>
      </div>

      {/* Day markers */}
      <div className="flex items-center gap-1 mb-2">
        {DAY_MARKERS.map((m) => {
          // A day is "claimed" if it's <= current streak progress
          const claimed = m.day <= info.currentStreak && (info.todayClaimed || m.day < displayedDay);
          const isToday = m.day === displayedDay && !info.todayClaimed;
          return (
            <div key={m.day} className="flex-1 flex flex-col items-center">
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold',
                claimed
                  ? 'bg-gold/20 border border-gold text-gold'
                  : isToday
                    ? 'bg-gold/10 border-2 border-gold/60 text-gold'
                    : m.bonus
                      ? 'bg-surface-3 border border-dashed border-gold/30 text-warm-gray/50'
                      : 'bg-surface-3 text-warm-gray/40',
              )}>
                {claimed
                  ? <CheckIcon className="w-3 h-3" />
                  : m.bonus
                    ? <StarIcon className="w-3 h-3 text-gold/40" />
                    : m.day}
              </div>
              <span className={cn('text-[8px] mt-0.5', claimed ? 'text-gold' : 'text-warm-gray/30')}>
                +{m.bonus ? m.baseCoins + m.bonus : m.baseCoins}
              </span>
            </div>
          );
        })}
      </div>

      {/* Toast (shown briefly after claim) */}
      {toast && (
        <div className="mb-2 px-2 py-1 rounded bg-gold/15 border border-gold/30 text-[10px] text-gold font-semibold flex items-center gap-1">
          <CoinIcon className="w-3 h-3" /> {toast}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-2 px-2 py-1 rounded bg-oxblood/15 border border-oxblood/30 text-[10px] text-oxblood">
          {error}
        </div>
      )}

      {/* Claim button or "claimed" state */}
      {!info.todayClaimed ? (
        <button
          onClick={handleClaim}
          disabled={claiming}
          className={cn(
            'w-full py-1.5 rounded font-bold text-[11px] transition-all flex items-center justify-center gap-1.5',
            claiming
              ? 'bg-surface-3 text-warm-gray/50 cursor-not-allowed'
              : 'bg-gold/15 border border-gold/30 text-gold hover:bg-gold/25',
          )}
        >
          {claiming ? 'Claiming...' : (
            <>
              Claim Day {displayedDay}
              <span className="inline-flex items-center gap-0.5 text-[10px] bg-gold/20 px-1.5 py-0.5 rounded">
                <CoinIcon className="w-2.5 h-2.5" />
                +{nextDayCoins?.bonus ? nextDayCoins.baseCoins + nextDayCoins.bonus : nextDayCoins?.baseCoins ?? 15}
              </span>
            </>
          )}
        </button>
      ) : (
        <div className="text-center py-1 text-[10px] text-warm-gray/40 flex items-center justify-center gap-1">
          <CheckIcon className="w-3 h-3 text-verdigris" /> Claimed today &mdash; back tomorrow!
        </div>
      )}
    </div>
  );
}

export default StreakCard;
